import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { API_ENDPOINTS } from '../config/apiConfig';
import './Auth.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError(getTranslation(language, 'allFieldsRequired'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Handle network errors
      if (!response.ok && response.status === 0) {
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Try to parse JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Server error: Invalid response. Please try again later.');
      }

      if (!response.ok) {
        throw new Error(data.error || `Login failed (${response.status})`);
      }

      // Validate response has required fields
      if (!data.token || !data.user) {
        throw new Error('Invalid server response: missing token or user data');
      }

      // Save token and user data
      login(data.token, data.user);

      // Redirect to dashboard
      navigate('/practice');
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h2>{getTranslation(language, 'login')}</h2>
        <p className="auth-subtitle">{getTranslation(language, 'login')} {getTranslation(language, 'dontHaveAccount')}</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{getTranslation(language, 'email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={getTranslation(language, 'email')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{getTranslation(language, 'password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={getTranslation(language, 'password')}
              disabled={loading}
            />
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? getTranslation(language, 'loading') : getTranslation(language, 'login')}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            {getTranslation(language, 'dontHaveAccount')} <Link to="/register">{getTranslation(language, 'register')}</Link>
          </p>
          <p>
            <Link to="/" className="auth-home-link">← {getTranslation(language, 'home')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
